import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'gokuri-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly mediaQueryList = this.isBrowser
    ? this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)') ?? null
    : null;

  private readonly _systemPrefersDark = signal(this.mediaQueryList?.matches ?? false);
  private readonly _mode = signal<ThemeMode>(this.readStoredMode());
  private hasInitialized = false;

  readonly mode = this._mode.asReadonly();
  readonly systemPrefersDark = this._systemPrefersDark.asReadonly();
  readonly effectiveTheme = computed(() => {
    const mode = this._mode();
    if (mode === 'system') {
      return this._systemPrefersDark() ? 'dark' : 'light';
    }
    return mode;
  });

  init(): void {
    if (!this.isBrowser || this.hasInitialized) return;
    this.hasInitialized = true;
    this._systemPrefersDark.set(this.mediaQueryList?.matches ?? false);
    this.applyThemeAttribute(this._mode());
    this.setupSystemPreferenceListener();
    effect(() => {
      const mode = this._mode();
      this.persistMode(mode);
      this.applyThemeAttribute(mode);
    });
  }

  setTheme(mode: ThemeMode): void {
    this._mode.set(mode);
  }

  toggleDarkLight(): void {
    const next = this.effectiveTheme() === 'dark' ? 'light' : 'dark';
    this._mode.set(next);
  }

  private setupSystemPreferenceListener(): void {
    if (!this.mediaQueryList) return;
    const handler = (event: MediaQueryListEvent) => {
      this._systemPrefersDark.set(event.matches);
    };

    if (typeof this.mediaQueryList.addEventListener === 'function') {
      this.mediaQueryList.addEventListener('change', handler);
      this.destroyRef.onDestroy(() => this.mediaQueryList?.removeEventListener('change', handler));
      return;
    }

    this.mediaQueryList.addListener(handler);
    this.destroyRef.onDestroy(() => this.mediaQueryList?.removeListener(handler));
  }

  private readStoredMode(): ThemeMode {
    if (!this.isBrowser) return 'system';
    const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return 'system';
  }

  private persistMode(mode: ThemeMode): void {
    if (!this.isBrowser) return;
    this.document.defaultView?.localStorage.setItem(STORAGE_KEY, mode);
  }

  private applyThemeAttribute(mode: ThemeMode): void {
    const root = this.document.documentElement;
    root.setAttribute('data-theme', mode);
  }
}
